import styled, { createGlobalStyle } from 'styled-components';
import { LayoutConst } from 'core';
import { SIDEBAR_WIDTH } from '../constants';

export const DisablePullToRefresh = createGlobalStyle`
  html, body {
    overscroll-behavior-y: none;
    scrollbar-width: none;
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar {
    display: none;
  }
`;

export const PresentationModeContainer = styled.div<{ backgroundColor?: string; isSidebarOpen?: boolean }>`
  width: 100%;
  height: 100%;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  display: flex;
  align-items: center;
  justify-content: ${({isSidebarOpen}) => isSidebarOpen ? 'flex-end' : 'center'};
  position: relative;
  overflow: hidden;
`;

export const SliderContainer = styled.div<{ isSidebarOpen?: boolean }>`
  width: ${({ isSidebarOpen }) => isSidebarOpen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%'};
  height: 100%;
  position: relative;

  .slick-slide.slick-current img:hover,
  .slick-slide.slick-current [data-fullscreen-image]:hover {
    cursor: zoom-in;
  }

  .slick-slide.slick-current [data-card-block] img:hover {
    cursor: pointer;
  }

  .slick-slide {
    pointer-events: none;
  }

  .slick-slide.slick-current {
    pointer-events: auto;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 236px)' : '100%'};
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 196px)' : '100%'};
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    overflow: hidden;

    .slick-slider {
      position: absolute;
      top: 0;
      left: 100%;
      width: 100dvh;
      height: 100dvw;
      transform-origin: top left;
      transform: rotate(90deg);
    }

    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }
  }
`;

export const FullscreenImageOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 11000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  cursor: zoom-out;
`;

export const FullscreenImage = styled.img`
  width: auto;
  height: auto;
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  display: block;
  cursor: default;
`;

export const SlideWrapper = styled.div<{ isActive?: boolean }>`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  pointer-events: ${({ isActive }) => (isActive ? 'auto' : 'none')};

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 100%;
  }
`;

export const BlockContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: center;
`;

export const ExitPresentationButton = styled.button`
  border: none;
  border-radius: 10px;
  background: #343434;
  color: #fefefe;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.2px;
  padding: 10px 16px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.86;
  }

  &:active {
    opacity: 0.72;
  }
`;

export const ButtonsWrapper = styled.div<{isSidebarOpen?: boolean }>`
  position: fixed;
  width: ${({ isSidebarOpen }) => isSidebarOpen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%'};
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 24px;
  box-sizing: border-box;
  z-index: 10000;
  bottom: 0;
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 236px)' : '100%'};
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 196px)' : '100%'};
    padding: 0 20px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: fixed;
    top: 0;
    left: 48px;
    bottom: auto;
    width: 100dvh;
    transform-origin: top left;
    transform: rotate(90deg);
    justify-content: center;
    padding: 0 16px calc(20px + env(safe-area-inset-right, 0px));
  }
`;

export const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  @media (max-width: ${LayoutConst.screen.m}px) {
    gap: 12px;
  }
`;

export const RightControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  @media (max-width: ${LayoutConst.screen.m}px) {
    gap: 12px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    justify-content: center;
    position: relative;
    gap: 0;
  }
`;

export const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 12px;
  background: #343434;
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 2px;
    gap: 4px;
    border-radius: 8px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    margin: 0 auto;
  }
`;

export const NavButtonLeft = styled.button`
  display: flex;
  padding: 8px 10px 8px 6px;
  align-items: center;
  border-radius: 8px;
  background: #545453;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 6px 7px 6px 5px;
    svg { width: 18px; height: 18px; }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const NavButtonRight = styled.button`
  display: flex;
  padding: 8px 6px 8px 10px;
  align-items: center;
  border-radius: 8px;
  background: #545453;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 6px 5px 6px 7px;
    svg { width: 18px; height: 18px; }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const NavCounter = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background: rgba(52, 52, 52, 0.5);
  backdrop-filter: blur(2px);
  color: #fff;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;
`;

export const MobileNavButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    display: flex;
    flex: 0 0 16px;
  }
`;

export const MobileNavCounterText = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

export const ShrinkButton = styled.button`
  background: #343434;
  border: none;
  cursor: pointer;
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover svg {
    opacity: 0.8;
  }

  &:active svg {
    opacity: 0.6;
  }

  svg {
    transition: opacity 0.2s;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 8px;
    border-radius: 8px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    right: 0;
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    margin-right: 24px;
  }
`;

export const MobileShrinkIconWrap = styled.div`
  display: flex;
  padding: 10.5px;
  align-items: center;
  border-radius: 10.5px;
  background: rgba(52, 52, 52, 0.5);
  backdrop-filter: blur(2px);
`;

export const SidebarButton = styled.button`
  display: flex;
  padding: 12px;
  align-items: center;
  border-radius: 12px;
  background: #343434;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 8px;
    border-radius: 8px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const SidebarPanel = styled.div<{ isOpen: boolean; backgroundColor?: string }>`
  position: fixed;
  left: 0;
  top: 0;
  width: ${SIDEBAR_WIDTH}px;
  height: 100%;
  padding: 16px 8px 8px 16px;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  z-index: 9999;
  box-sizing: border-box;
  overflow-y: auto;
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isOpen }) => isOpen ? '1' : '0'};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: #5d5d5d;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 236px;
    padding: 16px 8px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 196px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const SlidesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SlideThumbnail = styled.div<{ isActive?: boolean }>`
  width: 280px;
  height: 158px;
  border-radius: 3px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  border: ${({ isActive }) => isActive ? '5px solid #F1DB63' : 'none'};
  box-sizing: border-box;
  background: #f0f0f0;
  a, button {
    pointer-events: none !important;
  }
  
  &:hover {
    opacity: 0.8;
  }
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 200px;
    height: 113px;
    border: ${({ isActive }) => isActive ? '4px solid #F1DB63' : 'none'};
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 160px;
    height: 90px;
    border: ${({ isActive }) => isActive ? '3px solid #F1DB63' : 'none'};
  }
`;

export const ThumbnailContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  transform: scale(0.146);
  transform-origin: top left;
  pointer-events: none;
  overflow: hidden;
`;

export const SlideNumberWrapper = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  display: flex;
  padding: 4px 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: #F1DB63;
  z-index: 10;
`;

export const SlideNumber = styled.div`
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  color: #151514;
`;

export const HeightAdjustmentStyles = createGlobalStyle`
    .slick-slider, .slick-list, .slick-track, .slick-slide, .slick-slide > div {
      height: 100%;
    }
    ${SlideWrapper} {
      height: 100%;
    }
`;import styled, { createGlobalStyle } from 'styled-components';
import { LayoutConst } from 'core';
import { SIDEBAR_WIDTH } from '../constants';

export const DisablePullToRefresh = createGlobalStyle`
  html, body {
    overscroll-behavior-y: none;
    scrollbar-width: none;
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar {
    display: none;
  }
`;

export const PresentationModeContainer = styled.div<{ backgroundColor?: string; isSidebarOpen?: boolean }>`
  width: 100%;
  height: 100%;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  display: flex;
  align-items: center;
  justify-content: ${({isSidebarOpen}) => isSidebarOpen ? 'flex-end' : 'center'};
  position: relative;
  overflow: hidden;
`;

export const SliderContainer = styled.div<{ isSidebarOpen?: boolean }>`
  width: ${({ isSidebarOpen }) => isSidebarOpen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%'};
  height: 100%;
  position: relative;

  .slick-slide.slick-current img:hover,
  .slick-slide.slick-current [data-fullscreen-image]:hover {
    cursor: zoom-in;
  }

  .slick-slide.slick-current [data-card-block] img:hover {
    cursor: pointer;
  }

  .slick-slide {
    pointer-events: none;
  }

  .slick-slide.slick-current {
    pointer-events: auto;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 236px)' : '100%'};
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 196px)' : '100%'};
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    overflow: hidden;

    .slick-slider {
      position: absolute;
      top: 0;
      left: 100%;
      width: 100dvh;
      height: 100dvw;
      transform-origin: top left;
      transform: rotate(90deg);
    }

    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }
  }
`;

export const FullscreenImageOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 11000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  cursor: zoom-out;
`;

export const FullscreenImage = styled.img`
  width: auto;
  height: auto;
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  display: block;
  cursor: default;
`;

export const SlideWrapper = styled.div<{ isActive?: boolean }>`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  pointer-events: ${({ isActive }) => (isActive ? 'auto' : 'none')};

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 100%;
  }
`;

export const BlockContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: center;
`;

export const ExitPresentationButton = styled.button`
  border: none;
  border-radius: 10px;
  background: #343434;
  color: #fefefe;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.2px;
  padding: 10px 16px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.86;
  }

  &:active {
    opacity: 0.72;
  }
`;

export const ButtonsWrapper = styled.div<{isSidebarOpen?: boolean }>`
  position: fixed;
  width: ${({ isSidebarOpen }) => isSidebarOpen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%'};
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 24px;
  box-sizing: border-box;
  z-index: 10000;
  bottom: 0;
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 236px)' : '100%'};
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: ${({ isSidebarOpen }) => isSidebarOpen ? 'calc(100% - 196px)' : '100%'};
    padding: 0 20px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: fixed;
    top: 0;
    left: 48px;
    bottom: auto;
    width: 100dvh;
    transform-origin: top left;
    transform: rotate(90deg);
    justify-content: center;
    padding: 0 16px calc(20px + env(safe-area-inset-right, 0px));
  }
`;

export const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  @media (max-width: ${LayoutConst.screen.m}px) {
    gap: 12px;
  }
`;

export const RightControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  @media (max-width: ${LayoutConst.screen.m}px) {
    gap: 12px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    justify-content: center;
    position: relative;
    gap: 0;
  }
`;

export const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 12px;
  background: #343434;
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 2px;
    gap: 4px;
    border-radius: 8px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    margin: 0 auto;
  }
`;

export const NavButtonLeft = styled.button`
  display: flex;
  padding: 8px 10px 8px 6px;
  align-items: center;
  border-radius: 8px;
  background: #545453;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 6px 7px 6px 5px;
    svg { width: 18px; height: 18px; }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const NavButtonRight = styled.button`
  display: flex;
  padding: 8px 6px 8px 10px;
  align-items: center;
  border-radius: 8px;
  background: #545453;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 6px 5px 6px 7px;
    svg { width: 18px; height: 18px; }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const NavCounter = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background: rgba(52, 52, 52, 0.5);
  backdrop-filter: blur(2px);
  color: #fff;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;
`;

export const MobileNavButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    display: flex;
    flex: 0 0 16px;
  }
`;

export const MobileNavCounterText = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

export const ShrinkButton = styled.button`
  background: #343434;
  border: none;
  cursor: pointer;
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover svg {
    opacity: 0.8;
  }

  &:active svg {
    opacity: 0.6;
  }

  svg {
    transition: opacity 0.2s;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 8px;
    border-radius: 8px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    right: 0;
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    margin-right: 24px;
  }
`;

export const MobileShrinkIconWrap = styled.div`
  display: flex;
  padding: 10.5px;
  align-items: center;
  border-radius: 10.5px;
  background: rgba(52, 52, 52, 0.5);
  backdrop-filter: blur(2px);
`;

export const SidebarButton = styled.button`
  display: flex;
  padding: 12px;
  align-items: center;
  border-radius: 12px;
  background: #343434;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 8px;
    border-radius: 8px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const SidebarPanel = styled.div<{ isOpen: boolean; backgroundColor?: string }>`
  position: fixed;
  left: 0;
  top: 0;
  width: ${SIDEBAR_WIDTH}px;
  height: 100%;
  padding: 16px 8px 8px 16px;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  z-index: 9999;
  box-sizing: border-box;
  overflow-y: auto;
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isOpen }) => isOpen ? '1' : '0'};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: #5d5d5d;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 236px;
    padding: 16px 8px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 196px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

export const SlidesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SlideThumbnail = styled.div<{ isActive?: boolean }>`
  width: 280px;
  height: 158px;
  border-radius: 3px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  border: ${({ isActive }) => isActive ? '5px solid #F1DB63' : 'none'};
  box-sizing: border-box;
  background: #f0f0f0;
  a, button {
    pointer-events: none !important;
  }
  
  &:hover {
    opacity: 0.8;
  }
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 200px;
    height: 113px;
    border: ${({ isActive }) => isActive ? '4px solid #F1DB63' : 'none'};
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 160px;
    height: 90px;
    border: ${({ isActive }) => isActive ? '3px solid #F1DB63' : 'none'};
  }
`;

export const ThumbnailContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  transform: scale(0.146);
  transform-origin: top left;
  pointer-events: none;
  overflow: hidden;
`;

export const SlideNumberWrapper = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  display: flex;
  padding: 4px 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: #F1DB63;
  z-index: 10;
`;

export const SlideNumber = styled.div`
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  color: #151514;
`;

export const HeightAdjustmentStyles = createGlobalStyle`
    .slick-slider, .slick-list, .slick-track, .slick-slide, .slick-slide > div {
      height: 100%;
    }
    ${SlideWrapper} {
      height: 100%;
    }
`;